import React from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { colors, typography } from '../../../styles/theme';
import { FILTER_DROPDOWN_HEIGHT, FILTER_PANEL_DIVIDER, FILTER_ACCENT, FILTER_ACCENT_CONTRAST, FILTER_ACCENT_10 } from './FilterPanel.styles';

const triggerStyle = ({ isOpen = false, isActive = false, disabled = false } = {}) => ({
  width: '100%',
  height: `${FILTER_DROPDOWN_HEIGHT}px`,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  padding: '0 12px',
  borderRadius: '6px',
  border: `1px solid ${isOpen || isActive ? FILTER_ACCENT : colors.border}`,
  backgroundColor: colors.backgroundSurface,
  color: disabled ? 'rgba(255,255,255,0.35)' : colors.textPanelDescription,
  fontSize: typography.fontSize.sm,
  fontFamily: typography.fontFamily.primary,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.7 : 1,
  textAlign: 'left',
});

const menuStyle = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  zIndex: 10,
  backgroundColor: colors.backgroundSurface,
  borderRadius: '6px',
  border: `1px solid ${colors.border}`,
  boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
  overflowY: 'auto',
  maxHeight: '220px',
};

const checkboxStyle = ({ checked = false } = {}) => ({
  width: '16px',
  height: '16px',
  borderRadius: '3px',
  border: `1.5px solid ${checked ? FILTER_ACCENT : colors.border}`,
  backgroundColor: checked ? FILTER_ACCENT : 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

const checkmarkStyle = {
  width: '8px',
  height: '5px',
  borderLeft: `2px solid ${FILTER_ACCENT_CONTRAST}`,
  borderBottom: `2px solid ${FILTER_ACCENT_CONTRAST}`,
  transform: 'rotate(-45deg) translateY(-1px)',
};

const searchInputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  // El menú scrollea: la búsqueda queda fija arriba.
  position: 'sticky',
  top: 0,
  zIndex: 1,
  padding: '8px 12px',
  border: 'none',
  borderBottom: `1px solid ${FILTER_PANEL_DIVIDER}`,
  backgroundColor: colors.backgroundSurface,
  color: colors.textPanelDescription,
  fontSize: typography.fontSize.sm,
  fontFamily: typography.fontFamily.primary,
  outline: 'none',
};

const hintStyle = {
  display: 'block',
  marginTop: '4px',
  fontSize: typography.fontSize.xs,
  fontFamily: typography.fontFamily.primary,
  color: colors.textSecondary,
};

const menuMessageStyle = {
  padding: '10px 12px',
  color: colors.textSecondary,
  fontSize: typography.fontSize.xs,
  fontFamily: typography.fontFamily.primary,
};

/** Compara sin acentos ni mayúsculas: "nueva cordoba" encuentra "Nueva Córdoba". */
const normalizeForSearch = (text) => String(text ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

function formatMultiDisplay(selectedValues, options, placeholder) {
  if (!selectedValues?.length) return null;
  if (selectedValues.length === 1) {
    const [value] = selectedValues;
    return options.find((o) => o.value === value)?.label || String(value) || placeholder;
  }
  return `${selectedValues.length} seleccionados`;
}

/**
 * Opciones a listar: las del contrato más las seleccionadas que ya no están
 * disponibles (pasa con las opciones que se leen del mapa al mover la vista),
 * para que siempre se puedan destildar desde el propio desplegable.
 */
function withSelectedOptions(options, selectedValues) {
  const missing = selectedValues
    .filter((value) => !options.some((o) => o.value === value))
    .map((value) => ({ key: `selected-${value}`, value, label: String(value) }));
  return missing.length ? [...missing, ...options] : options;
}

/** Select multi-opción con checkboxes internos y búsqueda opcional. */
function FilterMultiSelect({
  filterId,
  label,
  options,
  selectedValues = [],
  isOpen,
  onToggle,
  onChange,
  disabled = false,
  searchable = false,
  emptyHint = null,
  hint = null,
}) {
  const [hoveredIndex, setHoveredIndex] = React.useState(-1);
  const [query, setQuery] = React.useState('');
  const isActive = selectedValues.length > 0;
  const display = formatMultiDisplay(selectedValues, options, label);

  React.useEffect(() => {
    if (!isOpen) setQuery('');
  }, [isOpen]);

  const listedOptions = withSelectedOptions(options, selectedValues);
  const normalizedQuery = normalizeForSearch(query);
  const visibleOptions = normalizedQuery
    ? listedOptions.filter((o) => normalizeForSearch(o.label).includes(normalizedQuery))
    : listedOptions;

  const toggleValue = (value) => {
    const set = new Set(selectedValues);
    if (set.has(value)) set.delete(value);
    else set.add(value);
    const next = Array.from(set);
    onChange(next.length ? next : null);
  };

  return (
    <div data-filter-dropdown={filterId} style={{ position: 'relative', width: '100%' }}>
      <button
        type="button"
        onClick={disabled ? undefined : onToggle}
        style={triggerStyle({ isOpen, isActive, disabled })}
        aria-expanded={isOpen}
        disabled={disabled}
        title={hint || undefined}
      >
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {display || label}
        </span>
        {isOpen
          ? <KeyboardArrowUpIcon sx={{ fontSize: 20, color: FILTER_ACCENT }} />
          : <KeyboardArrowDownIcon sx={{ fontSize: 20, color: colors.textSecondary }} />}
      </button>
      {isOpen && !disabled && (
        <div role="listbox" style={menuStyle}>
          {searchable && (
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar…"
              style={searchInputStyle}
              aria-label={`Buscar en ${label}`}
            />
          )}
          {visibleOptions.length === 0 && (
            <div style={menuMessageStyle}>
              {listedOptions.length === 0
                ? (emptyHint || 'Sin opciones disponibles')
                : 'Sin resultados'}
            </div>
          )}
          {visibleOptions.map((option, idx) => {
            const checked = selectedValues.includes(option.value);
            return (
              <React.Fragment key={option.key}>
                {idx > 0 && <div style={{ height: 1, backgroundColor: FILTER_PANEL_DIVIDER }} />}
                <button
                  type="button"
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '9px 12px',
                    border: 'none',
                    backgroundColor: hoveredIndex === idx ? FILTER_ACCENT_10 : 'transparent',
                    color: colors.textPanelDescription,
                    fontSize: typography.fontSize.sm,
                    fontFamily: typography.fontFamily.primary,
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                  onClick={() => toggleValue(option.value)}
                  onMouseEnter={() => setHoveredIndex(idx)}
                  onMouseLeave={() => setHoveredIndex(-1)}
                >
                  <span style={checkboxStyle({ checked })}>
                    {checked && <span style={checkmarkStyle} aria-hidden />}
                  </span>
                  <span>{option.label}</span>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      )}
      {hint && <span style={hintStyle}>{hint}</span>}
    </div>
  );
}

export default FilterMultiSelect;
